import { useFormContext } from '@/lib/form-contexts'
import { useStore } from '@tanstack/react-form'
import {
  getHeroTextPositionOptions,
  heroLayoutOptions,
  heroLayoutOptionsWithInherited,
  heroTextToneOptions,
  INHERIT_HERO_LAYOUT,
} from './storefrontStyleOptions'

const fieldName = (root, field) => `${root}.${field}`

export function HeroMessageFields({ fieldRoot, onChange = null }) {
  const form = useFormContext()
  const titleField = fieldName(fieldRoot, 'title')
  const subtextField = fieldName(fieldRoot, 'subtext')
  const buttonTextField = fieldName(fieldRoot, 'buttonText')
  const handleCopyChange = () => onChange?.()

  return (
    <div className="space-y-4">
      <form.AppField name={titleField} listeners={{ onChange: handleCopyChange }}>
        {(field) => <field.TextInput label="Headline text" />}
      </form.AppField>
      <form.AppField name={subtextField} listeners={{ onChange: handleCopyChange }}>
        {(field) => <field.TextareaInput label="Supporting text" rows={3} />}
      </form.AppField>
      <form.AppField name={buttonTextField} listeners={{ onChange: handleCopyChange }}>
        {(field) => <field.TextInput label="Button label" />}
      </form.AppField>
    </div>
  )
}

export function HeroStyleField({ fieldRoot, allowInheritedLayout = false, onChange = null }) {
  const form = useFormContext()
  const layoutField = fieldName(fieldRoot, 'layout')
  const currentLayout = useStore(form.store, (state) => {
    const segments = layoutField.split('.')
    return segments.reduce((value, segment) => value?.[segment], state.values)
  })
  const showInheritedLayoutOption = allowInheritedLayout || currentLayout === INHERIT_HERO_LAYOUT
  const layoutOptions = showInheritedLayoutOption
    ? heroLayoutOptionsWithInherited
    : heroLayoutOptions

  const handleLayoutChange = (layout) => {
    const positionField = fieldName(fieldRoot, 'preferredTextPosition')
    if (layout === 'split' && form.getFieldValue(positionField) === 'center') {
      form.setFieldValue(positionField, 'right')
    }

    onChange?.()
  }

  return (
    <form.AppField
      name={layoutField}
      listeners={{ onChange: ({ value }) => handleLayoutChange(value) }}
    >
      {(field) => (
        <field.RadioButtonGroup
          label="Banner style"
          options={layoutOptions}
          joined={layoutOptions.length !== 4}
          classNames={{
            container: layoutOptions.length === 4 ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-3',
            button: layoutOptions.length === 4 ? 'w-full' : '',
          }}
        />
      )}
    </form.AppField>
  )
}

export function HeroTextPresentationFields({ fieldRoot, onChange = null }) {
  const form = useFormContext()
  const layoutField = fieldName(fieldRoot, 'layout')
  const positionField = fieldName(fieldRoot, 'preferredTextPosition')
  const toneField = fieldName(fieldRoot, 'preferredTextTone')
  const currentLayout = useStore(form.store, (state) => {
    const segments = layoutField.split('.')
    return segments.reduce((value, segment) => value?.[segment], state.values)
  })
  const positionOptions = getHeroTextPositionOptions(currentLayout)
  const handlePresentationChange = () => onChange?.()

  return (
    <div className="space-y-4">
      <form.AppField name={positionField} listeners={{ onChange: handlePresentationChange }}>
        {(field) => (
          <field.RadioButtonGroup
            label="Text placement"
            options={positionOptions}
            classNames={{
              container: positionOptions.length === 2 ? 'grid grid-cols-2' : 'grid grid-cols-3',
            }}
          />
        )}
      </form.AppField>
      <form.AppField name={toneField} listeners={{ onChange: handlePresentationChange }}>
        {(field) => (
          <field.RadioButtonGroup
            label="Text contrast"
            options={heroTextToneOptions}
            classNames={{ container: 'grid grid-cols-3' }}
          />
        )}
      </form.AppField>
    </div>
  )
}

export function HeroPresentationFields({
  fieldRoot,
  allowInheritedLayout = false,
  onChange = null,
}) {
  return (
    <div className="space-y-4 border-t pt-5">
      <HeroStyleField
        fieldRoot={fieldRoot}
        allowInheritedLayout={allowInheritedLayout}
        onChange={onChange}
      />
      <HeroTextPresentationFields fieldRoot={fieldRoot} onChange={onChange} />
    </div>
  )
}

export function HomepageBannerCompositionFields({
  fieldRoot,
  allowInheritedLayout = false,
  buttonDestination = null,
  onChange = null,
}) {
  return (
    <div className="space-y-4">
      <HeroMessageFields fieldRoot={fieldRoot} onChange={onChange} />
      {buttonDestination}
      <HeroPresentationFields
        fieldRoot={fieldRoot}
        allowInheritedLayout={allowInheritedLayout}
        onChange={onChange}
      />
    </div>
  )
}
